import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Landmark, TrendingUp, TrendingDown, Flame, Info,
} from "lucide-react";
import { API } from "../config/api";
import TradeSinceSparkline from "../components/TradeSinceSparkline";
import TrumpTrades from "../components/TrumpTrades";

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};
const fmtNum = (n) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString());
const fmtBand = (lo, hi) => {
  if (!hi) return "—";
  return `$${(lo / 1000).toFixed(0)}K-$${(hi / 1000).toFixed(0)}K`;
};

// Map a weight % (0..max) to a heatmap intensity (Tailwind class)
function heatTone(weightPct, maxWeight) {
  const r = Math.min(1, weightPct / Math.max(maxWeight, 1));
  if (r > 0.66) return "bg-emerald-600/80 text-white";
  if (r > 0.33) return "bg-emerald-500/60 text-white";
  if (r > 0.15) return "bg-emerald-700/40 text-emerald-100";
  if (r > 0.05) return "bg-emerald-800/30 text-emerald-200";
  return "bg-slate-800/60 text-slate-300";
}
function sellTone(rowSells, maxSells) {
  const r = Math.min(1, rowSells / Math.max(maxSells, 1));
  if (r > 0.66) return "border-red-500";
  if (r > 0.33) return "border-red-600/70";
  if (r > 0.1) return "border-red-700/50";
  return "border-slate-700";
}

export default function PoliticalTradesPage() {
  const [hot, setHot] = useState(null);
  const [hotDays, setHotDays] = useState(7);
  const [heat, setHeat] = useState(null);
  const [heatDays, setHeatDays] = useState(60);
  const [byPol, setByPol] = useState(null);
  const [polDays, setPolDays] = useState(120);
  const [polChamber, setPolChamber] = useState("all"); // "all" | "house" | "senate"
  const [lb, setLb] = useState(null);
  const [lbDays, setLbDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let canc = false;

    // ─── Hydrate from localStorage FIRST so the page renders instantly
    // even on a cold backend (computing:true scenario after fresh deploy).
    const LS_KEYS = {
      hot:    `sp_pt_hot_${hotDays}`,
      heat:   `sp_pt_heat_${heatDays}`,
      bypol:  `sp_pt_bypol_${polDays}`,
      lb:     `sp_pt_lb_${lbDays}`,
    };
    let hadAnyCache = false;
    try {
      for (const [k, lsKey] of Object.entries(LS_KEYS)) {
        const c = JSON.parse(localStorage.getItem(lsKey) || "null");
        if (c?.data) {
          hadAnyCache = true;
          if (k === "hot") setHot(c.data);
          else if (k === "heat") setHeat(c.data);
          else if (k === "bypol") setByPol(c.data);
          else if (k === "lb") setLb(c.data);
        }
      }
    } catch {}
    if (hadAnyCache) setLoading(false);

    (async () => {
      if (!hadAnyCache) setLoading(true);
      // Use Promise.allSettled so a single slow/failed endpoint doesn't 502 the
      // whole page. Each section can render with whatever data landed.
      const results = await Promise.allSettled([
        axios.get(`${API}/political-trades/hot-this-week?days=${hotDays}&limit=5`, { timeout: 20000 }),
        axios.get(`${API}/political-trades/heatmap?days=${heatDays}&top_n=60`, { timeout: 15000 }),
        axios.get(`${API}/political-trades/by-politician?days=${polDays}&limit_filers=40`, { timeout: 15000 }),
        axios.get(`${API}/political-trades/leaderboard?days=${lbDays}&limit=20`, { timeout: 15000 }),
      ]);
      if (canc) return;
      const [h, hm, bp, l] = results;
      const persist = (key, payload) => {
        // Only persist payloads that actually have data (avoid caching the
        // empty `{computing:true, rows:[]}` placeholder).
        const hasData =
          payload && (
            (payload.rows && payload.rows.length) ||
            (payload.filers && payload.filers.length) ||
            (payload.items && payload.items.length)
          );
        if (hasData) {
          try { localStorage.setItem(key, JSON.stringify({ data: payload, ts: Date.now() })); } catch {}
        }
      };
      if (h.status === "fulfilled")  { setHot(h.value.data);     persist(LS_KEYS.hot, h.value.data); }
      if (hm.status === "fulfilled") { setHeat(hm.value.data);   persist(LS_KEYS.heat, hm.value.data); }
      if (bp.status === "fulfilled") { setByPol(bp.value.data);  persist(LS_KEYS.bypol, bp.value.data); }
      if (l.status === "fulfilled")  { setLb(l.value.data);      persist(LS_KEYS.lb, l.value.data); }
      setLoading(false);
    })();
    return () => { canc = true; };
  }, [hotDays, heatDays, polDays, lbDays]);

  // Leaderboard cold-cache poll: if the backend says `computing:true`, retry
  // every 8s so the UI lights up automatically once the score lands.
  useEffect(() => {
    if (!lb || lb.ready || !lb.computing) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/political-trades/leaderboard?days=${lbDays}&limit=20`,
          { timeout: 20000 }
        );
        if (r.data?.ready) {
          setLb(r.data);
          clearInterval(id);
        }
      } catch {/* keep polling */}
    }, 8000);
    return () => clearInterval(id);
  }, [lb, lbDays]);

  // Heatmap cold-cache poll: same pattern as leaderboard. The heatmap endpoint
  // returns {computing:true, rows:[]} immediately and finishes in ~10s, so
  // the UI re-fetches every 5s until ready.
  useEffect(() => {
    if (!heat || heat.ready || !heat.computing) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/political-trades/heatmap?days=${heatDays}&top_n=60`,
          { timeout: 25000 }
        );
        if (r.data?.ready) {
          setHeat(r.data);
          clearInterval(id);
        }
      } catch {/* keep polling */}
    }, 5000);
    return () => clearInterval(id);
  }, [heat, heatDays]);

  // By-Politician cold-cache poll: heavier compute (~30-60s), so re-fetch
  // every 8s. Mirrors the leaderboard polling pattern.
  useEffect(() => {
    if (!byPol || byPol.ready || !byPol.computing) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/political-trades/by-politician?days=${polDays}&limit_filers=40`,
          { timeout: 30000 }
        );
        if (r.data?.ready) {
          setByPol(r.data);
          clearInterval(id);
        }
      } catch {/* keep polling */}
    }, 8000);
    return () => clearInterval(id);
  }, [byPol, polDays]);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading congressional PTR data…
      </div>
    );
  }
  if (err) return <div className="p-8 text-red-400">Error: {err}</div>;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6" data-testid="political-trades-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Landmark className="w-8 h-8 text-emerald-400" /> Political Trades
          </h1>
          <p className="text-slate-400 mt-1 text-sm md:text-base">
            US House &amp; Senate Periodic Transaction Reports (STOCK Act PTR). Parsed live from{" "}
            <span className="text-slate-300 font-mono text-xs">disclosures-clerk.house.gov</span>{" "}
            and{" "}
            <span className="text-slate-300 font-mono text-xs">efdsearch.senate.gov</span>.
          </p>
        </div>
        <Badge variant="outline" className="border-slate-700 text-slate-300">
          {byPol?.filers?.length || 0} active filers · {heat?.rows?.length || 0} tickers · last 60d
        </Badge>
        {/* House vs Senate filer breakdown — surfaces the new Senate integration */}
        {byPol?.filers?.length > 0 && (
          <div className="flex items-center gap-2 text-xs" data-testid="chamber-counts">
            <span className="bg-sky-500/15 border border-sky-500/40 text-sky-200 rounded px-2 py-0.5">
              🏛️ House: {byPol.filers.filter((f) => (f.chamber || "house") === "house").length}
            </span>
            <span className="bg-amber-500/15 border border-amber-500/40 text-amber-200 rounded px-2 py-0.5">
              🏛️ Senate: {byPol.filers.filter((f) => f.chamber === "senate").length}
            </span>
          </div>
        )}
        {/* Empty-state escape hatch: when the production deploy has just
            restarted and MongoDB is still empty, this lets the user kick off
            all three ingestors (House + Senate + Trump) without waiting for
            the 60-90s background-task initial delay. */}
        {(byPol?.filers?.length || 0) === 0 && (heat?.rows?.length || 0) === 0 && (
          <button
            data-testid="political-trades-force-refresh"
            onClick={async () => {
              try {
                await axios.post(`${API}/trump-trades/refresh-all`, null, { timeout: 240000 });
                window.location.reload();
              } catch {
                window.location.reload();
              }
            }}
            className="text-xs px-3 py-1.5 rounded border border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-800/40"
          >
            ⚡ Force-refresh ingest (House + Senate + Trump)
          </button>
        )}
      </div>

      {/* Methodology disclosure */}
      <div className="rounded-lg border border-amber-700/40 bg-amber-950/30 p-3 text-xs text-amber-200/90 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-semibold">Methodology:</span> PTRs disclose dollar
          AMOUNT BANDS (e.g.{" "}
          <span className="font-mono">$1,001–$15,000</span>), not exact share counts. We display
          BOTH the literal band AND the band-midpoint estimate (≈shares = midpoint ÷ today's price).
          Realized gain/loss is approximated as matched buy-sell midpoint pairs. Filings can be up
          to 45 days old (STOCK Act window).
        </div>
      </div>

      <Tabs defaultValue="hot" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
          <TabsTrigger value="hot" data-testid="ptab-hot">🔥 Hot in Congress</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="ptab-heatmap">📊 Heatmap</TabsTrigger>
          <TabsTrigger value="by-politician" data-testid="ptab-bypol">👥 By Politician</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="ptab-leaderboard">🏆 Leaderboard</TabsTrigger>
          <TabsTrigger value="trump" data-testid="ptab-trump">🇺🇸 Trump Trades</TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* TAB 1 — HOT THIS WEEK                                        */}
        {/* ============================================================ */}
        <TabsContent value="hot" className="space-y-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Flame className="w-5 h-5 text-orange-400" /> Hot in Congress this week
              </CardTitle>
              <div className="flex items-center gap-1 text-xs">
                {[7, 14, 30].map((d) => (
                  <button
                    key={d}
                    data-testid={`hot-days-${d}`}
                    onClick={() => setHotDays(d)}
                    className={`px-2 py-1 rounded border ${
                      hotDays === d
                        ? "bg-emerald-700/50 border-emerald-500 text-white"
                        : "border-slate-700 text-slate-400 hover:text-white"
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="text-xs text-slate-400 -mt-2 mb-2">
              Top 5 tickers by NET BUY-minus-SELL midpoint swing in the last {hotDays} days.
              Politicians often cluster on the same name a few weeks before unusual price moves.
            </CardContent>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="hot-table">
                  <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                    <tr>
                      <th className="text-left py-2 px-2">#</th>
                      <th className="text-left py-2 px-2">Ticker</th>
                      <th className="text-right py-2 px-2">Net swing ≈</th>
                      <th className="text-right py-2 px-2">Buys</th>
                      <th className="text-right py-2 px-2">Sells</th>
                      <th className="text-right py-2 px-2">Filers</th>
                      <th className="text-right py-2 px-2">Today</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(hot?.rows || []).map((r, i) => (
                      <tr key={r.ticker} className="border-b border-slate-800 hover:bg-slate-800/30">
                        <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                        <td className="py-2 px-2">
                          <a
                            href={`/stock?ticker=${r.ticker}`}
                            className="font-mono font-bold text-emerald-300 hover:underline"
                            data-testid={`hot-ticker-${r.ticker}`}
                          >
                            {r.ticker}
                          </a>
                        </td>
                        <td className={`py-2 px-2 text-right font-semibold ${
                          r.net_dollar_swing > 0 ? "text-emerald-300" : "text-red-300"
                        }`}>
                          {r.net_dollar_swing > 0 ? "+" : ""}
                          {fmtMoney(r.net_dollar_swing)}
                        </td>
                        <td className="py-2 px-2 text-right text-emerald-400">
                          {r.buys}
                          <span className="block text-[10px] text-slate-500">
                            {fmtMoney(r.buy_mid)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-red-400">
                          {r.sells}
                          <span className="block text-[10px] text-slate-500">
                            {fmtMoney(r.sell_mid)}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-slate-300">{r.n_filers}</td>
                        <td className="py-2 px-2 text-right text-slate-200">
                          {r.current_price ? `$${r.current_price.toFixed(2)}` : "—"}
                        </td>
                      </tr>
                    ))}
                    {(hot?.rows || []).length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-6 text-slate-500">
                          {hot?.computing
                            ? "🔄 Scoring hot tickers… (auto-refreshing every 8s)"
                            : `No qualifying trades in the last ${hotDays} days.`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* TAB 2 — HEATMAP                                              */}
        {/* ============================================================ */}
        <TabsContent value="heatmap" className="space-y-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                📊 Congressional Holdings Heatmap
              </CardTitle>
              <div className="flex items-center gap-1 text-xs">
                {[30, 60, 120].map((d) => (
                  <button
                    key={d}
                    data-testid={`heat-days-${d}`}
                    onClick={() => setHeatDays(d)}
                    className={`px-2 py-1 rounded border ${
                      heatDays === d
                        ? "bg-emerald-700/50 border-emerald-500 text-white"
                        : "border-slate-700 text-slate-400 hover:text-white"
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="text-xs text-slate-400 -mt-2 mb-2">
              Tile size & green intensity = aggregate BUY midpoint $ (% of total Congress
              buying). Red border thickness = aggregate SELL pressure. Click any tile to open
              the ticker.
            </CardContent>
            <CardContent>
              <HeatGrid heat={heat} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* TAB 3 — BY POLITICIAN                                        */}
        {/* ============================================================ */}
        <TabsContent value="by-politician" className="space-y-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white text-lg">👥 Trades by Politician</CardTitle>
              <div className="flex items-center gap-3 text-xs">
                {/* Chamber filter — House/Senate/All */}
                <div className="flex items-center gap-1" data-testid="chamber-filter">
                  {[
                    { k: "all", label: "All" },
                    { k: "house", label: "House" },
                    { k: "senate", label: "Senate" },
                  ].map((c) => (
                    <button
                      key={c.k}
                      data-testid={`chamber-filter-${c.k}`}
                      onClick={() => setPolChamber(c.k)}
                      className={`px-2 py-1 rounded border ${
                        polChamber === c.k
                          ? c.k === "senate"
                            ? "bg-amber-700/40 border-amber-500 text-amber-100"
                            : c.k === "house"
                            ? "bg-sky-700/40 border-sky-500 text-sky-100"
                            : "bg-emerald-700/50 border-emerald-500 text-white"
                          : "border-slate-700 text-slate-400 hover:text-white"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {[60, 120, 365].map((d) => (
                    <button
                      key={d}
                      data-testid={`pol-days-${d}`}
                      onClick={() => setPolDays(d)}
                      className={`px-2 py-1 rounded border ${
                        polDays === d
                          ? "bg-emerald-700/50 border-emerald-500 text-white"
                          : "border-slate-700 text-slate-400 hover:text-white"
                      }`}
                    >
                      {d === 365 ? "1y" : `${d}d`}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(byPol?.filers || [])
                .filter((f) => polChamber === "all" || (f.chamber || "house") === polChamber)
                .map((f) => (
                  <FilerCard key={f.filer} filer={f} />
                ))}
              {(byPol?.filers || []).filter(
                (f) => polChamber === "all" || (f.chamber || "house") === polChamber
              ).length === 0 && (
                <div className="text-center text-slate-500 py-6">
                  {byPol?.computing
                    ? "🔄 Scoring filer portfolios… (auto-refreshing every 8s)"
                    : `No ${polChamber === "all" ? "" : `${polChamber} `}filings in the last ${polDays} days.`}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {/* ============================================================ */}
        {/* TAB 4 — LEADERBOARD (top performing politician traders)      */}
        {/* ============================================================ */}
        <TabsContent value="leaderboard" className="space-y-3">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader className="flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white text-lg">🏆 Top performing politician traders</CardTitle>
              <div className="flex items-center gap-1 text-xs">
                {[60, 90, 180, 365].map((d) => (
                  <button
                    key={d}
                    data-testid={`lb-days-${d}`}
                    onClick={() => setLbDays(d)}
                    className={`px-2 py-1 rounded border ${
                      lbDays === d
                        ? "bg-emerald-700/50 border-emerald-500 text-white"
                        : "border-slate-700 text-slate-400 hover:text-white"
                    }`}
                  >
                    {d === 365 ? "1y" : `${d}d`}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="text-xs text-slate-400 -mt-2 mb-2">
              Politicians ranked by midpoint-$-weighted % price change of their disclosed BUYS
              from tx_date to today. Includes BOTH House and Senate. Filers must have ≥3 buys
              in the window (filters out one-off lucky picks).
            </CardContent>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leaderboard-table">
                  <thead className="text-xs uppercase text-slate-400 border-b border-slate-700">
                    <tr>
                      <th className="text-left py-2 px-2">#</th>
                      <th className="text-left py-2 px-2">Politician</th>
                      <th className="text-left py-2 px-2">Chamber</th>
                      <th className="text-right py-2 px-2">Score</th>
                      <th className="text-right py-2 px-2">Buys</th>
                      <th className="text-right py-2 px-2">$ Bought</th>
                      <th className="text-left py-2 px-2 hidden md:table-cell">Top picks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lb?.rows || []).map((r, i) => (
                      <tr key={r.filer} className="border-b border-slate-800 hover:bg-slate-800/30">
                        <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                        <td className="py-2 px-2 text-white font-semibold">
                          {r.filer}
                          <span className="block text-[10px] text-slate-500 font-normal">{r.state}</span>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase ${
                            r.chamber === "senate"
                              ? "bg-purple-900/50 text-purple-300"
                              : "bg-blue-900/50 text-blue-300"
                          }`}>
                            {r.chamber}
                          </span>
                        </td>
                        <td className={`py-2 px-2 text-right font-bold tabular-nums ${
                          r.weighted_pct_change >= 0 ? "text-emerald-300" : "text-red-300"
                        }`}>
                          {r.weighted_pct_change >= 0 ? "+" : ""}
                          {r.weighted_pct_change.toFixed(2)}%
                        </td>
                        <td className="py-2 px-2 text-right text-slate-300">{r.n_buys}</td>
                        <td className="py-2 px-2 text-right text-slate-400">
                          {fmtMoney(r.total_buy_mid)}
                        </td>
                        <td className="py-2 px-2 hidden md:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {(r.scored_trades || []).slice(0, 4).map((t, ti) => (
                              <a
                                key={ti}
                                href={`/stock?ticker=${t.ticker}`}
                                className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                                  t.pct_change >= 0
                                    ? "bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60"
                                    : "bg-red-900/40 text-red-300 hover:bg-red-900/60"
                                }`}
                                title={`${t.ticker} ${t.tx_date}: $${t.buy_price} → $${t.now_price}`}
                              >
                                {t.ticker} {t.pct_change >= 0 ? "+" : ""}
                                {t.pct_change.toFixed(0)}%
                              </a>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(lb?.rows || []).length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-6 text-slate-500">
                          {lb?.computing
                            ? "🔄 Scoring politicians… (re-fetches every 8s, takes ~30-60s on cold cache)"
                            : "No qualifying filers yet."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* TAB 5 — TRUMP TRADES (OGE 278-T)                              */}
        {/* ============================================================ */}
        <TabsContent value="trump" className="space-y-3">
          <TrumpTrades />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =================================================================
// HEATMAP GRID — sized tiles, color = % of total congressional buying
// =================================================================
function HeatGrid({ heat }) {
  const rows = useMemo(() => heat?.rows || [], [heat]);
  const maxWeight = useMemo(() => Math.max(...rows.map((r) => r.weight_pct || 0), 1), [rows]);
  const maxSells = useMemo(() => Math.max(...rows.map((r) => r.sells || 0), 1), [rows]);

  if (!rows.length) {
    return (
      <div className="text-slate-500 text-center py-6">
        {heat?.computing
          ? "🔄 Building heatmap… (auto-refreshing every 5s)"
          : "No data yet."}
      </div>
    );
  }

  return (
    <div
      className="grid gap-1.5"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))" }}
      data-testid="heatmap-grid"
    >
      {rows.map((r) => {
        const tone = heatTone(r.weight_pct, maxWeight);
        const sellBorder = sellTone(r.sells, maxSells);
        const totalBuy = (r.house_buy_mid || 0) + (r.senate_buy_mid || 0) || r.buy_mid || 1;
        const housePct = Math.round(((r.house_buy_mid || 0) / totalBuy) * 100);
        const senatePct = 100 - housePct;
        return (
          <a
            key={r.ticker}
            href={`/stock?ticker=${r.ticker}`}
            data-testid={`heat-tile-${r.ticker}`}
            className={`relative block rounded p-2 border-2 ${sellBorder} ${tone} hover:ring-2 hover:ring-emerald-400 transition`}
            title={
              `${r.ticker}: ${r.buys} buys (${fmtMoney(r.buy_mid)}) / ` +
              `${r.sells} sells (${fmtMoney(r.sell_mid)}) · ` +
              `House ${r.n_house_filers || 0} filers (${fmtMoney(r.house_buy_mid || 0)}) · ` +
              `Senate ${r.n_senate_filers || 0} filers (${fmtMoney(r.senate_buy_mid || 0)}) · ` +
              `band: ${fmtBand(r.buy_band_low, r.buy_band_high)} buys, ` +
              `${fmtBand(r.sell_band_low, r.sell_band_high)} sells · ${r.n_filers} filers`
            }
          >
            <div className="font-mono font-bold text-sm">{r.ticker}</div>
            <div className="text-[10px] opacity-80">{r.weight_pct.toFixed(1)}%</div>
            <div className="text-[10px] mt-0.5 flex justify-between">
              <span className="text-emerald-200">{r.buys}B</span>
              <span className="text-red-200">{r.sells}S</span>
            </div>
            <div className="text-[9px] opacity-70 mt-0.5">{fmtMoney(r.buy_mid)}</div>
            {/* House (sky) vs Senate (amber) buy-share split bar */}
            <div className="mt-1 flex h-[3px] rounded overflow-hidden bg-slate-900/60">
              {housePct > 0 && (
                <div
                  className="bg-sky-400/80"
                  style={{ width: `${housePct}%` }}
                  title={`House: ${housePct}%`}
                />
              )}
              {senatePct > 0 && (
                <div
                  className="bg-amber-400/80"
                  style={{ width: `${senatePct}%` }}
                  title={`Senate: ${senatePct}%`}
                />
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
}

// =================================================================
// PER-POLITICIAN expandable card with portfolio holdings + raw trades
// =================================================================
function FilerCard({ filer }) {
  const [expanded, setExpanded] = useState(false);
  const heldValue = filer.portfolio_held_value_today_est;

  return (
    <div
      className="border border-slate-800 rounded-lg overflow-hidden"
      data-testid={`filer-${(filer.filer || '').replace(/\s+/g, '-')}`}
    >
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full p-3 bg-slate-900 hover:bg-slate-800 flex justify-between items-center text-left"
      >
        <div>
          <div className="font-semibold text-white flex items-center gap-2">
            {filer.filer}
            {filer.chamber === "senate" ? (
              <span
                data-testid={`chamber-${(filer.filer || '').replace(/\s+/g,'-')}`}
                className="text-[10px] uppercase tracking-wide bg-amber-500/20 border border-amber-500/40 text-amber-200 rounded px-1.5 py-0.5"
              >
                Senate
              </span>
            ) : (
              <span
                data-testid={`chamber-${(filer.filer || '').replace(/\s+/g,'-')}`}
                className="text-[10px] uppercase tracking-wide bg-sky-500/20 border border-sky-500/40 text-sky-200 rounded px-1.5 py-0.5"
              >
                House
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {filer.state} · {filer.n_buys} buys / {filer.n_sells} sells · disclosed{" "}
            {fmtMoney(filer.total_buy_mid)} buy / {fmtMoney(filer.total_sell_mid)} sell
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">≈ Portfolio value (today)</div>
          <div className={`font-bold text-lg ${
            heldValue >= 0 ? "text-emerald-400" : "text-red-400"
          }`}>
            {fmtMoney(heldValue)}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="p-3 bg-slate-950 space-y-3">
          {/* Holdings table */}
          <div>
            <div className="text-xs uppercase text-slate-500 mb-2 font-semibold">
              Estimated current holdings
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
                  <tr>
                    <th className="text-left py-1 px-2">Ticker</th>
                    <th className="text-right py-1 px-2">Buys</th>
                    <th className="text-right py-1 px-2">Sells</th>
                    <th className="text-right py-1 px-2">Cost basis ≈</th>
                    <th className="text-right py-1 px-2">≈ Shares held</th>
                    <th className="text-right py-1 px-2">@ price</th>
                    <th className="text-right py-1 px-2">≈ Value today</th>
                    <th className="text-right py-1 px-2">≈ Realized P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {filer.tickers.map((t) => (
                    <tr
                      key={t.ticker}
                      className="border-b border-slate-900 hover:bg-slate-900/40"
                      data-testid={`filer-${(filer.filer||'').replace(/\s+/g,'-')}-${t.ticker}`}
                    >
                      <td className="py-1 px-2">
                        <a
                          href={`/stock?ticker=${t.ticker}`}
                          className="font-mono text-emerald-300 hover:underline"
                        >
                          {t.ticker}
                        </a>
                      </td>
                      <td className="py-1 px-2 text-right text-emerald-400">{t.n_buys}</td>
                      <td className="py-1 px-2 text-right text-red-400">{t.n_sells}</td>
                      <td className="py-1 px-2 text-right text-slate-300">
                        {fmtMoney(t.held_cost_mid)}
                      </td>
                      <td className="py-1 px-2 text-right text-slate-300">
                        ≈ {fmtNum(t.est_shares_held)}
                      </td>
                      <td className="py-1 px-2 text-right text-slate-400">
                        {t.current_price ? `$${t.current_price.toFixed(2)}` : "—"}
                      </td>
                      <td className="py-1 px-2 text-right text-emerald-300">
                        {fmtMoney(t.held_value_today)}
                      </td>
                      <td className={`py-1 px-2 text-right ${
                        t.realized_pl_estimate > 0
                          ? "text-emerald-400"
                          : t.realized_pl_estimate < 0
                          ? "text-red-400"
                          : "text-slate-500"
                      }`}>
                        {t.realized_pl_estimate ? fmtMoney(t.realized_pl_estimate) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Raw trade list */}
          <div>
            <div className="text-xs uppercase text-slate-500 mb-2 font-semibold">
              Recent disclosed trades
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
                  <tr>
                    <th className="text-left py-1 px-2">Date</th>
                    <th className="text-left py-1 px-2">Ticker</th>
                    <th className="text-left py-1 px-2">Action</th>
                    <th className="text-left py-1 px-2">Disclosed band</th>
                    <th className="text-right py-1 px-2">≈ Midpoint</th>
                    <th className="text-left py-1 px-2 hidden md:table-cell">Since trade</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filer.trades]
                    .sort((a, b) => {
                      // Parse "MM/DD/YYYY" to a comparable timestamp for descending sort
                      const ts = (s) => {
                        if (!s) return 0;
                        const [m, d, y] = s.split("/").map((x) => parseInt(x, 10));
                        return new Date(y || 0, (m || 1) - 1, d || 1).getTime();
                      };
                      return ts(b.tx_date) - ts(a.tx_date);
                    })
                    .map((tr, i) => (
                    <tr key={i} className="border-b border-slate-900">
                      <td className="py-1 px-2 text-slate-400 font-mono text-[11px]">
                        {tr.tx_date}
                      </td>
                      <td className="py-1 px-2 font-mono">
                        <a
                          href={`/stock?ticker=${tr.ticker}`}
                          className="text-emerald-300 hover:text-emerald-200 hover:underline"
                          data-testid={`trade-ticker-link-${tr.ticker}-${i}`}
                        >
                          {tr.ticker}
                        </a>
                      </td>
                      <td className="py-1 px-2">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                          tr.action === "BUY"
                            ? "bg-emerald-900/50 text-emerald-300"
                            : tr.action === "SELL"
                            ? "bg-red-900/50 text-red-300"
                            : "bg-slate-800 text-slate-400"
                        }`}>
                          {tr.action}
                          {tr.tx_type?.includes("partial") && " (partial)"}
                        </span>
                      </td>
                      <td className="py-1 px-2 text-slate-300 font-mono text-[11px]">
                        ${tr.amount_low.toLocaleString()} – ${tr.amount_high.toLocaleString()}
                      </td>
                      <td className="py-1 px-2 text-right text-slate-400">
                        ≈ {fmtMoney(tr.amount_mid)}
                      </td>
                      <td className="py-1 px-2 text-slate-500 font-mono text-[11px] hidden md:table-cell">
                        <TradeSinceSparkline ticker={tr.ticker} txDate={tr.tx_date} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
